import type { SubscriptionTypes } from "@component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import type { AbstractLeadFormProps } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import type { SmeAccountLeadCalcRequest, SmeAccountLeadCalcResponse } from "./types";

/**
 * @Shortcode
 *
 * @title "Vállalkozói bankszámla leadform"
 * @shortcode "corporate_bank_account_leadform"
 * @slug "vallalati-bankszamla-leadform"
 * @type "leadform"
 */

interface Props extends AbstractLeadFormProps {
	responderVariant?: string;
}

interface FormFields extends AbstractLeadFormFields {
	companyName: string;
	question?: string;
}

export class CorporateBankAccountLeadForm extends AbstractLeadForm<
	SmeAccountLeadCalcRequest,
	SmeAccountLeadCalcResponse,
	FormFields,
	Props
> {
	// EventParams
	protected readonly productTypes: string = "SME_ACCOUNT";
	protected readonly componentType: EventComponentType = "leadform";
	protected readonly subscriptionTypes: SubscriptionTypes[] = ["CORPORATE_NEWSLETTER"];

	public static defaultProps = {
		title: "Kérd szakértőnk segítségét!",
		colorScheme: "light",
	};

	protected renderSteps(): ReactNode {
		const { title } = this.props;
		const {
			values: { email },
			submitState,
		} = this.state;

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget="success-lead"
					title={title}
				/>
				<Step.SuccessPage
					stepName="success-lead"
					userEmail={email}
					subscriptionTypes={this.subscriptionTypes}
				/>
			</>
		);
	}

	protected getLeadData(): LeadRequest<SmeAccountLeadCalcRequest, SmeAccountLeadCalcResponse> {
		const {
			values: { companyName, question },
		} = this.state;
		const data = super.getLeadData();

		data.request.companyName = companyName;
		data.request.question = question;

		return data;
	}

	protected onStep(target: string, withEvent: boolean): void {
		if (target === "success-lead") {
			this.sendLead()
				.then(() => {
					super.onStep(target, false);
				})
				.catch((error: Error) => {
					// TODO: Hibakezelés
					logError(error);
				});
		} else {
			super.onStep(target, withEvent);
		}
	}

	protected getAutoresponderName(): string {
		const { responderVariant } = this.props;

		return responderVariant === "unicredit" ? "autoresponder-unicredit-fixpont" : "autoresponder-sme-account";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { lastName, firstName },
		} = this.state;
		const { consultantName, consultantEmail } = this.props;

		return {
			lastName,
			firstName,
			consultantEmail,
			consultantName,
		};
	}

	protected getProductType(): ProductType {
		return "SME_ACCOUNT";
	}

	protected getComponentName(): string {
		return "CorporateBankAccountLeadForm";
	}
}

export default CorporateBankAccountLeadForm;
